import React, { useEffect, useMemo, useRef, useState } from "react";
import api, { API } from "../lib/api";
import { MapPin, CheckCircle2, ChevronRight, ChevronLeft, FileText, ExternalLink } from "lucide-react";
import SignaturePad from "../components/SignaturePad";
import PhotoCapture from "../components/PhotoCapture";
import { useAuth } from "../lib/auth";
import { fmtDateTime } from "../lib/datetime";

const SCAFFOLD_TYPES = ["Independent", "Tower", "Birdcage", "Staircase", "Other"];
const DUTY_CLASSES = ["Inspection (0.75 kN/m²)", "Light (1.5 kN/m²)", "General purpose (2.0 kN/m²)", "Heavy (2.5 kN/m²)", "Special (≥3.0 kN/m²)"];
const DEFAULT_LOAD = { "Inspection (0.75 kN/m²)": 0.75, "Light (1.5 kN/m²)": 1.5, "General purpose (2.0 kN/m²)": 2.0, "Heavy (2.5 kN/m²)": 2.5, "Special (≥3.0 kN/m²)": 3.0 };

const CONFORMITY = "Examined by a competent representative of Newell Scaffolding Ltd and conforms with: Health & Safety at Work etc. Act 1974; Work at Height Regulations 2005; Construction (Design and Management) Regulations 2015; BS EN 12811-1; TG20:21 and relevant NASC technical guidance.";

const HANDOVER_EVIDENCE_SLOTS = [
  { key: "scaff_tag",       label: "Scaff tag",           required: true,  hint: "The tag showing complete / safe to use" },
  { key: "ladder_gate",     label: "Ladder gate secured", required: false, hint: "Close-up of the secured gate" },
  { key: "base_soleplates", label: "Base / sole plates",  required: false, hint: "Foundation / spreader plates" },
  { key: "ties",            label: "Ties",                required: false, hint: "Representative tie into the structure" },
  { key: "general_view",    label: "General / full view", required: true,  hint: "Full scaffold in context" },
];

const blank = {
  structure_id: "",
  scaffold_type: "Independent",
  duty_class: "General purpose (2.0 kN/m²)",
  max_load_kn_m2: 2.0,
  ties_physical: 0,
  ties_reveal: 0,
  ties_anchor: 0,
  working_lifts: 1,
  tg20_compliant: true,
  bespoke_design_ref: "",
  scafftag_number: "",
  food_env_board_protection: false,
  food_env_tools_accounted: false,
  food_env_area_safe: false,
  newell_signature: "",
  newell_print_name: "",
  newell_position: "Competent person (TWC)",
  newell_cisrs_ref: "",
  issued_to: "McCain Whittlesey · Site engineer",
  photos: [],  // legacy — kept empty; the new named set below replaces it
  evidence_photos: {},  // { scaff_tag: dataUrl, ladder_gate: dataUrl, ... }
  gps_lat: null,
  gps_lng: null,
};

export default function Handover() {
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [structures, setStructures] = useState([]);
  const [scaffoldTypes, setScaffoldTypes] = useState([]);
  const [form, setForm] = useState({ ...blank, newell_print_name: user?.name || "" });
  const [gpsErr, setGpsErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [success, setSuccess] = useState(null);
  const fileRef = useRef();

  useEffect(() => {
    api.get("/structures").then((r) =>
      setStructures(r.data.filter((s) => ["Requested", "RAMS Approved", "DRA Signed", "Erected"].includes(s.status)))
    );
    api.get("/structure-types").then((r) => setScaffoldTypes(r.data.map((t) => t.name))).catch(() => setScaffoldTypes(SCAFFOLD_TYPES));
  }, []);

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const picked = useMemo(() => structures.find((s) => s.id === form.structure_id), [structures, form.structure_id]);

  // step gate checks
  const step1Ready = !!form.structure_id;
  const step2Ready = form.scaffold_type && form.duty_class && form.max_load_kn_m2 > 0 && form.working_lifts > 0 && (form.tg20_compliant || form.bespoke_design_ref.trim());
  // Required evidence photos: scaff_tag + general_view must be captured for step 3 to be ready.
  const requiredEvidenceOk = HANDOVER_EVIDENCE_SLOTS.filter((s) => s.required).every((s) => !!form.evidence_photos?.[s.key]);
  const step3Ready = form.food_env_board_protection && form.food_env_tools_accounted && form.food_env_area_safe && requiredEvidenceOk;
  const step4Ready = form.newell_signature && form.newell_print_name.trim();

  const captureGps = () => {
    setGpsErr("");
    if (!navigator.geolocation) { setGpsErr("Geolocation not supported"); return; }
    navigator.geolocation.getCurrentPosition(
      (p) => { setF("gps_lat", p.coords.latitude); setF("gps_lng", p.coords.longitude); },
      (e) => setGpsErr(e.message || "GPS denied"),
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  const setEvidencePhoto = (slotKey, dataUrl) => {
    setForm((f) => {
      const next = { ...(f.evidence_photos || {}) };
      if (dataUrl) next[slotKey] = dataUrl; else delete next[slotKey];
      return { ...f, evidence_photos: next };
    });
  };

  const submit = async () => {
    setBusy(true); setErr(""); setSuccess(null);
    try {
      const { data } = await api.post("/handovers", form);
      setSuccess(data);
      // reload structures list
      const lst = await api.get("/structures");
      setStructures(lst.data.filter((s) => ["Requested", "RAMS Approved", "DRA Signed", "Erected"].includes(s.status)));
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    }
    setBusy(false);
  };

  const reset = () => {
    setForm({ ...blank, newell_print_name: user?.name || "" });
    setStep(0);
    setSuccess(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  if (success) {
    return (
      <div style={{ padding: "24px 24px 64px", maxWidth: 720, margin: "0 auto" }} data-testid="handover-success-page">
        <div className="card" style={{ padding: 24, borderLeft: "6px solid #059669", background: "#ecfdf5" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, fontWeight: 800, color: "#065f46", fontSize: 18 }}>
            <CheckCircle2 size={22} /> Scaffold handover certificate issued
          </div>
          <div className="mono" style={{ marginTop: 6, color: "#065f46" }}>
            Certificate <b>{success.cert_ref}</b> · {fmtDateTime(success.issued_at)}
          </div>
        </div>

        <div className="card" style={{ padding: 20, marginTop: 16 }}>
          <h3 style={{ marginTop: 0 }}>Next steps</h3>
          <ol style={{ paddingLeft: 18, color: "#334155", lineHeight: 1.7 }}>
            <li>Download the PDF certificate and share with McCain site engineer.</li>
            <li>Have the McCain contact <b>countersign</b> the receipt — either on this device or via the structure detail page after issue.</li>
            <li>Structure auto-progressed; the 7-day inspection clock has started.</li>
          </ol>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
            <a
              data-testid="handover-pdf-link"
              className="btn-primary"
              href={`${API}/handovers/${success.id}/pdf`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 8 }}
            >
              <FileText size={16} /> View / download PDF
            </a>
            <a
              data-testid="handover-detail-link"
              className="btn-ghost"
              href={`/structures/${success.structure_id}`}
              style={{ textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6 }}
            >
              Open structure <ExternalLink size={14} />
            </a>
            <button data-testid="handover-new" className="btn-ghost" onClick={reset}>Issue another</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="handover-page" style={{ padding: "24px 24px 96px", maxWidth: 720, margin: "0 auto" }}>
      <div className="mono" style={{ color: "#ff5a00", fontWeight: 600, fontSize: 12, letterSpacing: "0.1em" }}>SCAFFOLD HANDOVER CERTIFICATE</div>
      <h1 style={{ fontSize: 26, fontWeight: 900, margin: "4px 0 0" }}>Issue handover</h1>
      <div style={{ color: "#64748b", marginBottom: 18 }}>4-step certificate · TG20:21 conformity · digital sign-off · PDF export</div>

      <Steps current={step} labels={["Structure", "Compliance", "Site checks", "Sign & issue"]} />

      <div className="card" style={{ padding: 20, marginTop: 16 }}>
        {step === 0 ? (
          <Step1 form={form} setF={setF} structures={structures} picked={picked} />
        ) : step === 1 ? (
          <Step2 form={form} setF={setF} scaffoldTypes={scaffoldTypes} />
        ) : step === 2 ? (
          <Step3 form={form} setF={setF} setEvidencePhoto={setEvidencePhoto} gpsErr={gpsErr} captureGps={captureGps} />
        ) : (
          <Step4 form={form} setF={setF} />
        )}
      </div>

      {err ? <div data-testid="handover-error" className="badge badge-red" style={{ padding: "8px 12px", marginTop: 12 }}>{String(err)}</div> : null}

      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 16, gap: 8 }}>
        <button data-testid="handover-back" className="btn-ghost" disabled={step === 0} onClick={() => setStep((s) => s - 1)}>
          <ChevronLeft size={14} style={{ verticalAlign: -2 }} /> Back
        </button>
        {step < 3 ? (
          <button
            data-testid="handover-next"
            className="btn-primary"
            disabled={(step === 0 && !step1Ready) || (step === 1 && !step2Ready) || (step === 2 && !step3Ready)}
            onClick={() => setStep((s) => s + 1)}
            style={{ height: 48, minWidth: 140 }}
          >
            Next <ChevronRight size={14} style={{ verticalAlign: -2 }} />
          </button>
        ) : (
          <button
            data-testid="handover-submit"
            className="btn-primary"
            disabled={!step4Ready || busy}
            onClick={submit}
            style={{ height: 48, minWidth: 220, fontWeight: 800 }}
          >
            {busy ? "Issuing…" : "Issue handover certificate"}
          </button>
        )}
      </div>
    </div>
  );
}

// ---------- Step components ----------
function Steps({ current, labels }) {
  return (
    <div style={{ display: "flex", gap: 6, marginTop: 14 }} data-testid="handover-steps">
      {labels.map((l, i) => (
        <div key={l} style={{ flex: 1 }} data-testid={`step-${i}`}>
          <div style={{ height: 4, background: i <= current ? "#ff5a00" : "#e2e8f0" }} />
          <div className="mono" style={{ marginTop: 6, fontSize: 11, color: i === current ? "#0f172a" : "#94a3b8", fontWeight: 600 }}>
            {String(i + 1).padStart(2, "0")} · {l.toUpperCase()}
          </div>
        </div>
      ))}
    </div>
  );
}

const Label = ({ children }) => (
  <label style={{ display: "block", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: "#475569", marginBottom: 4 }}>{children}</label>
);

function Step1({ form, setF, structures, picked }) {
  return (
    <div>
      <Label>Structure ready for handover</Label>
      <select data-testid="handover-structure" className="input-base" style={{ height: 52 }} value={form.structure_id} onChange={(e) => setF("structure_id", e.target.value)}>
        <option value="">— Select a structure —</option>
        {structures.map((s) => (
          <option key={s.id} value={s.id}>{s.area_name} · {s.description} · {s.status}</option>
        ))}
      </select>
      {picked ? (
        <div className="card" style={{ marginTop: 14, padding: 14, background: "#f8fafc" }} data-testid="picked-structure-card">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <Info label="Description" value={picked.description} />
            <Info label="Area" value={`${picked.area_name} · ${picked.zone_classification}`} />
            <Info label="Rate ref" value={picked.price_schedule_ref} mono />
            <Info label="Quantity" value={`${picked.quantity} ${picked.unit}`} mono />
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Step2({ form, setF, scaffoldTypes }) {
  const types = scaffoldTypes && scaffoldTypes.length ? scaffoldTypes : SCAFFOLD_TYPES;
  return (
    <div style={{ display: "grid", gap: 14 }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div>
          <Label>Scaffold type</Label>
          <select data-testid="hc-scaffold-type" className="input-base" value={form.scaffold_type} onChange={(e) => setF("scaffold_type", e.target.value)}>
            {types.map((t) => <option key={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <Label>Duty class</Label>
          <select
            data-testid="hc-duty-class"
            className="input-base"
            value={form.duty_class}
            onChange={(e) => {
              setF("duty_class", e.target.value);
              const def = DEFAULT_LOAD[e.target.value];
              if (def) setF("max_load_kn_m2", def);
            }}
          >
            {DUTY_CLASSES.map((t) => <option key={t}>{t}</option>)}
          </select>
        </div>
      </div>

      <div style={{ background: "#fffbeb", border: "1px solid #f59e0b", padding: 12 }}>
        <Label>Max load per platform (kN/m²) · THIS LOAD MUST NOT BE EXCEEDED</Label>
        <input data-testid="hc-max-load" type="number" step="0.1" min="0" className="input-base" value={form.max_load_kn_m2} onChange={(e) => setF("max_load_kn_m2", parseFloat(e.target.value) || 0)} />
      </div>

      <div>
        <Label>Ties (physical / reveal / anchor)</Label>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
          <input data-testid="hc-ties-physical" className="input-base" type="number" min="0" value={form.ties_physical} onChange={(e) => setF("ties_physical", parseInt(e.target.value || 0))} placeholder="Physical" />
          <input data-testid="hc-ties-reveal" className="input-base" type="number" min="0" value={form.ties_reveal} onChange={(e) => setF("ties_reveal", parseInt(e.target.value || 0))} placeholder="Reveal" />
          <input data-testid="hc-ties-anchor" className="input-base" type="number" min="0" value={form.ties_anchor} onChange={(e) => setF("ties_anchor", parseInt(e.target.value || 0))} placeholder="Anchor" />
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div>
          <Label>Working lifts (count)</Label>
          <input data-testid="hc-lifts" className="input-base" type="number" min="1" value={form.working_lifts} onChange={(e) => setF("working_lifts", parseInt(e.target.value || 1))} />
        </div>
        <div>
          <Label>Scafftag number</Label>
          <input data-testid="hc-scafftag" className="input-base" value={form.scafftag_number} onChange={(e) => setF("scafftag_number", e.target.value)} placeholder="e.g. ST-2026-0451" />
        </div>
      </div>

      <div>
        <Label>TG20:21 conformity</Label>
        <div style={{ display: "flex", gap: 8 }}>
          <button type="button" data-testid="hc-tg20-yes" onClick={() => setF("tg20_compliant", true)} className={form.tg20_compliant ? "btn-primary" : "btn-ghost"} style={{ flex: 1, height: 44 }}>TG20:21 compliant</button>
          <button type="button" data-testid="hc-tg20-no" onClick={() => setF("tg20_compliant", false)} className={!form.tg20_compliant ? "btn-primary" : "btn-ghost"} style={{ flex: 1, height: 44 }}>Bespoke design</button>
        </div>
        {!form.tg20_compliant ? (
          <div style={{ marginTop: 8 }}>
            <Label>Bespoke design reference (required)</Label>
            <input data-testid="hc-bespoke-ref" className="input-base" value={form.bespoke_design_ref} onChange={(e) => setF("bespoke_design_ref", e.target.value)} placeholder="e.g. NSW-DES-2026-014" />
          </div>
        ) : null}
      </div>
    </div>
  );
}

function Step3Check({ form, setF, k, label, testid }) {
  return (
    <label style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", border: "1px solid #cbd5e1", background: form[k] ? "#ecfdf5" : "white", cursor: "pointer" }} data-testid={testid}>
      <input type="checkbox" checked={form[k]} onChange={(e) => setF(k, e.target.checked)} style={{ width: 22, height: 22, accentColor: "#059669" }} />
      <span style={{ fontWeight: 600 }}>{label}</span>
    </label>
  );
}

function Step3({ form, setF, setEvidencePhoto, gpsErr, captureGps }) {
  const capturedCount = HANDOVER_EVIDENCE_SLOTS.filter((s) => !!form.evidence_photos?.[s.key]).length;
  return (
    <div style={{ display: "grid", gap: 14 }}>
      <div>
        <Label>Food-environment confirmations (all three required)</Label>
        <div style={{ display: "grid", gap: 8 }}>
          <Step3Check form={form} setF={setF} k="food_env_board_protection" label="Board / inlay protection over production" testid="hc-food-board" />
          <Step3Check form={form} setF={setF} k="food_env_tools_accounted" label="Tools accounted for + area cleaned down" testid="hc-food-tools" />
          <Step3Check form={form} setF={setF} k="food_env_area_safe" label="Area safe for handover" testid="hc-food-safe" />
        </div>
      </div>

      <div>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 4 }}>
          <Label>Evidence photographs</Label>
          <span className="mono" style={{ fontSize: 11, color: "#64748b" }} data-testid="hc-evidence-progress">
            {capturedCount} / {HANDOVER_EVIDENCE_SLOTS.length} captured
          </span>
        </div>
        <div style={{ fontSize: 12, color: "#64748b", marginBottom: 8 }}>
          Take the shots the certificate needs. <strong>Scaff tag</strong> and <strong>General view</strong> are required; the rest are strongly recommended.
        </div>
        <div style={{ display: "grid", gap: 12 }}>
          {HANDOVER_EVIDENCE_SLOTS.map((slot) => (
            <div key={slot.key} data-testid={`hc-evidence-slot-${slot.key}`} style={{ border: "1px solid #e2e8f0", borderRadius: 4, padding: 10, background: form.evidence_photos?.[slot.key] ? "#f0fdf4" : "#fafafa" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>
                    {slot.label}
                    {slot.required ? <span style={{ color: "#dc2626", marginLeft: 6 }}>*</span> : <span style={{ color: "#94a3b8", marginLeft: 6, fontWeight: 400, fontSize: 11 }}>optional</span>}
                  </div>
                  <div style={{ fontSize: 11, color: "#64748b" }}>{slot.hint}</div>
                </div>
                {form.evidence_photos?.[slot.key] ? (
                  <span className="mono" data-testid={`hc-evidence-status-${slot.key}`} style={{ fontSize: 10, color: "#065f46", background: "#d1fae5", padding: "2px 6px", borderRadius: 3, letterSpacing: "0.08em" }}>✓ CAPTURED</span>
                ) : null}
              </div>
              <PhotoCapture
                value={form.evidence_photos?.[slot.key] || null}
                onChange={(dataUrl) => setEvidencePhoto(slot.key, dataUrl)}
                testidPrefix={`hc-evidence-${slot.key}`}
                height={180}
              />
            </div>
          ))}
        </div>
      </div>

      <div>
        <Label>GPS location</Label>
        <button type="button" className="btn-ghost" style={{ width: "100%", height: 44 }} onClick={captureGps} data-testid="hc-gps-btn">
          <MapPin size={14} style={{ marginRight: 6, verticalAlign: -2 }} /> {form.gps_lat ? "Re-capture GPS" : "Capture GPS"}
        </button>
        {form.gps_lat ? <div className="mono" data-testid="hc-gps-display" style={{ marginTop: 6, color: "#065f46", fontSize: 12 }}>✓ {form.gps_lat.toFixed(5)}, {form.gps_lng.toFixed(5)}</div> : null}
        {gpsErr ? <div className="badge badge-amber" style={{ marginTop: 6, padding: "4px 8px" }}>{gpsErr} — handover will proceed without GPS</div> : null}
      </div>
    </div>
  );
}

function Step4({ form, setF }) {
  return (
    <div style={{ display: "grid", gap: 14 }}>
      <div className="card" style={{ padding: 12, background: "#f8fafc" }}>
        <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "#475569" }}>Conformity statement</div>
        <div data-testid="conformity-statement" style={{ marginTop: 6, fontSize: 13, color: "#334155", lineHeight: 1.5 }}>{CONFORMITY}</div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div>
          <Label>Newell print name</Label>
          <input data-testid="hc-newell-name" className="input-base" value={form.newell_print_name} onChange={(e) => setF("newell_print_name", e.target.value)} />
        </div>
        <div>
          <Label>Newell position</Label>
          <input data-testid="hc-newell-position" className="input-base" value={form.newell_position} onChange={(e) => setF("newell_position", e.target.value)} />
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div>
          <Label>CISRS card ref</Label>
          <input data-testid="hc-newell-cisrs" className="input-base" value={form.newell_cisrs_ref} onChange={(e) => setF("newell_cisrs_ref", e.target.value)} placeholder="e.g. CISRS 123456" />
        </div>
        <div>
          <Label>Issued to (McCain contact)</Label>
          <input data-testid="hc-issued-to" className="input-base" value={form.issued_to} onChange={(e) => setF("issued_to", e.target.value)} />
        </div>
      </div>

      <div>
        <Label>Newell competent-person signature (required)</Label>
        <SignaturePad value={form.newell_signature} onChange={(v) => setF("newell_signature", v)} testid="hc-newell-signature" />
      </div>

      <div className="mono" style={{ fontSize: 11, color: "#94a3b8" }}>
        On submit: certificate PDF is generated · structure auto-progresses · McCain countersignature can be captured from the structure detail page.
      </div>
    </div>
  );
}

const Info = ({ label, value, mono }) => (
  <div>
    <div style={{ fontSize: 10.5, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: "#475569" }}>{label}</div>
    <div className={mono ? "mono" : ""} style={{ fontSize: 14, fontWeight: 600, marginTop: 2 }}>{value}</div>
  </div>
);
